import { AfterViewChecked, ChangeDetectionStrategy, ChangeDetectorRef, Component, CUSTOM_ELEMENTS_SCHEMA, ElementRef, EventEmitter, HostListener, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Subscription } from 'rxjs';
import { debounceTime, tap } from 'rxjs/operators';
import { ShareModule } from '../../share.module';
import { SearchPartner } from '../../../models/search-partner.model';
import { Reference } from '../../../models/reference';

@Component({
	selector: 'auto-complete',
	templateUrl: './auto-complete.component.html',
	styleUrls: ['./auto-complete.component.scss'],
	standalone: true,
	imports:[ShareModule],
	schemas:[CUSTOM_ELEMENTS_SCHEMA],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class AutoCompleteComponent implements OnChanges, AfterViewChecked {
	resultListWidth = '0';
	showResults = false;
	isLoading: boolean = false;
	selectedItems: Reference[] = [];
@Input() inputControl!: FormControl;
@Input() searchArray: Reference[] = [];
@Input() defaultValue?: string;
@Input() label!: string;
@Input() displayField: 'id' | 'name' = 'id';
@Input() valueField?: 'id' | 'name';
@Input() multiSelect = false;


@Output() selected = new EventEmitter();
@Output() searchClick = new EventEmitter();
@Output() inputChanged = new EventEmitter<string>();

@ViewChild('input') input!: ElementRef;

subscription!: Subscription;


	@HostListener('window:resize', ['$event'])
	onResize(event: any) {
		this.resultListWidth = `${+this.input.nativeElement.offsetWidth + 2}`;
	}
	constructor(readonly cdr: ChangeDetectorRef) {}

	ngAfterViewChecked(): void {
		this.resultListWidth = `${+this.input.nativeElement.offsetWidth + 2}`;
	}

	ngOnChanges(changes: SimpleChanges): void {

		if(!changes['searchArray']){
			this.isLoading = false;
		}
		this.subscription?.unsubscribe();

		if(changes['searchArray']){
			let value = changes['searchArray'].currentValue;
			if(value != changes['searchArray'].previousValue && changes['searchArray'].previousValue !== undefined){
				if (value?.length) {
					this.showResultsOnlyOnUserInput();
					this.cdr.markForCheck();
				} 
				else if(value?.length == 0){
					this.isLoading = false;
					this.showResults = true;
				}
				else {  
					this.defaultValue = undefined;
					this.cdr.markForCheck();
				}
			}
		}
		this.subscription = this.inputControl.valueChanges.pipe(
			tap(s => this.isLoading = true),
			debounceTime(1000)
		).subscribe((value) => {
			this.searchInputChanged(value)
			this.isLoading = false;
			this.cdr.markForCheck();
		})
	}

showResultsOnlyOnUserInput() {
  this.isLoading = false;
  this.showResults = !!(this.inputControl.value && this.searchArray?.length);
  setTimeout(() => this.cdr.markForCheck(), 0);
}

	closeResults() {
		this.showResults = false
	}

	onInputInteraction() {
		const hasInputValue = !!String(this.inputControl?.value ?? '').trim();
		const hasFetchedResults = !!this.searchArray?.length;

		if (hasInputValue || hasFetchedResults) {
			this.showResults = true;
			this.cdr.markForCheck();
		}
	}

	onSelect(item: Reference, event?: any) {
		const isActive = event?.target.active
		const emitField = this.valueField || this.displayField;
		if (this.multiSelect && isActive) {
			this.showResults = true;
			this.selectedItems.push(item);
		} else if (!isActive) {
			this.selectedItems = this.selectedItems.filter(i => i[emitField] !== item[emitField]);
		}
		this.selected.emit(this.selectedItems.map(i => i[emitField]));
		this.setDisplayValue(this.selectedItems.map(i => i[this.displayField]).join(', '));
	}

	setDisplayValue(value: string){
		this.inputControl.setValue(value, { emitEvent: false, onlySelf: true })
	}

	isActive(item: Reference): boolean {
		const emitField = this.displayField;
		const normalize = (value: unknown): string => String(value ?? '').trim().toLowerCase();
		const itemDisplayValue = normalize(item[this.displayField]);

		if (this.selectedItems.some(selectedItem => normalize(selectedItem[emitField]) === itemDisplayValue)) {
			return true;
		}

		const currentValue = String(this.inputControl?.value || this.defaultValue || '');
		if (!currentValue.trim()) {
			return false;
		}

		const selectedValues = currentValue
			.split(',')
			.map(value => normalize(value))
			.filter(Boolean);

		return selectedValues.includes(itemDisplayValue);
	}

	searchInputChanged(value: any) {
  if (value) {
    this.inputChanged.emit(value);
    this.showResults = true; // Show on valid input
  }

  if (value === '' || value == null) {
    this.isLoading = false;
    this.searchArray = [];
    this.selected.emit(undefined);
    this.defaultValue = undefined;
    this.showResults = false; // ✅ Ensures it hides
    this.cdr.markForCheck();
  }
}

	clearSelection() {
  this.showResults = false;
  this.defaultValue = undefined;
  this.inputControl.setValue(null);
  this.searchArray = [];
  this.selectedItems = [];
  this.selected.emit('');
}


	onSearchClick() {
		this.searchClick.emit();
	}  
}